import { useQuery, useQueryClient } from "@tanstack/react-query"
import { api, toApiMessage } from "../../api/client"
import type { FamilyMember } from "../../api/types"
import { AudienceSelector } from "../../components/family"
import { showToast } from "../../components/ui"
import { useFamily } from "../../family/FamilyContext"

export type Audience = { memberIds: string[]; groupIds: string[] }

export type AudienceGroup = {
  id: string
  name: string
  isSystem: boolean
  memberships: Array<{ memberId: string }>
}

export function useAudienceOptions() {
  const { activeFamily } = useFamily()
  const familyId = activeFamily?.id ?? ""
  const members = useQuery({
    queryKey: ["members", familyId],
    queryFn: () => api.get<{ items: FamilyMember[] }>(`/families/${familyId}/members`),
    enabled: Boolean(familyId)
  })
  const groups = useQuery({
    queryKey: ["v1", familyId, "groups"],
    queryFn: () => api.get<{ items: AudienceGroup[] }>(`/families/${familyId}/groups`),
    enabled: Boolean(familyId)
  })
  return {
    familyId,
    members: (members.data?.items ?? []).filter((member) => member.status === "ACTIVE"),
    groups: groups.data?.items ?? [],
    isLoading: members.isLoading || groups.isLoading,
    error: members.error ?? groups.error
  }
}

export function audienceMemberIds(audience: Audience, groups: AudienceGroup[]) {
  return [
    ...new Set([
      ...audience.memberIds,
      ...groups
        .filter((group) => audience.groupIds.includes(group.id))
        .flatMap((group) => group.memberships.map((membership) => membership.memberId))
    ])
  ]
}

export function AudienceField({
  label,
  value,
  onChange,
  membersOnly = false
}: {
  label: string
  value: Audience
  onChange(value: Audience): void
  membersOnly?: boolean
}) {
  const client = useQueryClient()
  const { familyId, members, groups } = useAudienceOptions()

  async function createGroup() {
    const name = window.prompt("Название новой группы")?.trim()
    if (!name) return
    try {
      const group = await api.post<AudienceGroup>(`/families/${familyId}/groups`, {
        name,
        memberIds: value.memberIds
      })
      await client.invalidateQueries({ queryKey: ["v1", familyId, "groups"] })
      onChange({ memberIds: [], groupIds: [...new Set([...value.groupIds, group.id])] })
      showToast(`Группа «${name}» создана и выбрана`)
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    }
  }

  return (
    <AudienceSelector
      label={label}
      value={value}
      onChange={onChange}
      members={members.map((member) => ({ id: member.id, displayName: member.displayName }))}
      groups={groups.map((group) => ({
        id: group.id,
        name: group.name,
        isSystem: group.isSystem,
        memberIds: group.memberships.map((membership) => membership.memberId)
      }))}
      membersOnly={membersOnly}
      onCreateGroup={!membersOnly && value.memberIds.length ? () => void createGroup() : undefined}
    />
  )
}
